/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import React from 'react'
import {useIntl} from 'react-intl'
import {useBrand} from '../../contexts/brand-context'
import {InfoCircle} from '../../components/icons'
import {Box} from '@salesforce/retail-react-app/app/components/shared/ui'

export default function useLoginFields({
    form: {
        control,
        formState: {errors}
    },
    prefix = ''
}) {
    const {formatMessage} = useIntl()
    const {isGarage} = useBrand()

    const renderRememberMeLabel = () => (
        <Box display="flex" alignItems="center" gap="4px" fontSize="12px" fontWeight="400">
            {formatMessage({
                defaultMessage: 'Keep me signed in',
                id: 'use_login_fields.label.rememberme'
            })}
            <InfoCircle width="20px" height="20px" />
        </Box>
    )

    const fields = {
        email: {
            name: `${prefix}email`,
            label: formatMessage({
                defaultMessage: 'Email Address',
                id: 'use_login_fields.label.email'
            }),
            placeholder: formatMessage({
                defaultMessage: 'Email Address',
                id: 'use_login_fields.label.email'
            }),
            defaultValue: '',
            type: 'email',
            autoComplete: 'email',
            rules: {
                required: isGarage
                    ? formatMessage({
                          defaultMessage: 'Please fill out this field.',
                          id: 'use_login_fields.error.required_email.garage'
                      })
                    : formatMessage({
                          defaultMessage: 'Please fill out this field.',
                          id: 'use_login_fields.error.required_email.dynamite'
                      }),
                pattern: {
                    value: /^\S+@\S+\.\S{2,3}$/,
                    message: isGarage
                        ? formatMessage({
                              defaultMessage: 'Enter a valid email address such as name@email.com.',
                              id: 'use_login_fields.error.pattern_email.garage'
                          })
                        : formatMessage({
                              defaultMessage: 'Enter a valid email address such as name@email.com.',
                              id: 'use_login_fields.error.pattern_email.dynamite'
                          })
                }
            },
            inputProps: {
                maxLength: 50
            },
            error: errors?.[`${prefix}email`],
            control,
            variant: 'webFloatingField'
        },
        password: {
            name: `${prefix}password`,
            label: formatMessage({
                defaultMessage: 'Password',
                id: 'use_login_fields.label.password'
            }),
            placeholder: formatMessage({
                defaultMessage: 'Password',
                id: 'use_login_fields.label.password'
            }),
            defaultValue: '',
            type: 'password',
            rules: {
                required: isGarage
                    ? formatMessage({
                          defaultMessage: 'Please enter your password.',
                          id: 'use_login_fields.error.required_password.garage'
                      })
                    : formatMessage({
                          defaultMessage: 'Please enter your password.',
                          id: 'use_login_fields.error.required_password.dynamite'
                      })
            },
            inputProps: {
                maxLength: 50
            },
            error: errors?.[`${prefix}password`],
            control,
            variant: 'webFloatingField'
        },
        rememberme: {
            name: `${prefix}rememberme`,
            label: renderRememberMeLabel(),
            type: 'checkbox',
            defaultValue: false,
            error: errors[`${prefix}rememberme`],
            control
        }
    }
    return fields
}
